<template lang="pug">

section(
    v-if="statePortal"
    :class="['customer-portal-container']"
  )

  StaticAlertComponent(
    v-if="showAdvice"
    :alert="alertData"
    class="static-alert"
  )

  p(class="intro-text" v-html="$t('components.customer_portal.introduction')")

  //- Email input
  ValidationObserver(
      ref="emailInput"
      v-slot="{ validate }"
      class="email-field"
    )
    LexonTextFieldComponent(
      v-model="email"
      label="Email"
      id="email"
      name="email"
      type="text"
      validationRules="required|email"
      :disabled="!canSave"
    )
  div(class="container-buttons")
    button(
      v-if="credentialsSent && canSave"
      class="secondary-btn"
      @click.stop="askIfWantToDeleteAccess"
    )
      span(:class='icons.remove')
      span {{ this.$t('components.customer_portal.remove_access') }}

    button(
      v-if="accessExists && canSave"
      :class="[{ 'activate-access': blockedAccess }, 'secondary-btn']"
      @click.stop="lockOrUnlockPortalAccess"
    )
      span(:class='icons.lock')
      span {{ renderedSecondaryButtonText }}

  div(class="btn-area")
    button(
      v-if="canSave"
      class="primary-btn"
      @click.stop="executeMainAction"
    ) {{ renderedPrimaryButtonText }}

</template>

<script lang="ts">
import { Mixins, Component, Watch, Prop } from 'vue-property-decorator'
import { Icons } from '@/icons/icons'
import LexonTextFieldComponent from '@/components/forms/fields/LexonTextField/LexonTextFieldComponent.vue'
import { ValidationObserver } from 'vee-validate'
import { Action, Getter } from 'vuex-class'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { DialogTypes } from '@/store/modules/dialog/dialogTypes'
import { StatePortal, CustomerPortal } from '@/store/modules/customerPortal/customerPortalTypes'
import PermissionsMixin from '@/mixins/PermissionsMixin.vue'
import { AlertsData, AlertsTypes } from '@/store/modules/alerts/alertsTypes'
import StaticAlertComponent from '@/components/Alert/StaticAlertComponent.vue'

const dialogModule = ModuleNamespaces.DIALOG
const customerPortalModule = ModuleNamespaces.CUSTOMER_PORTAL
const authModule = ModuleNamespaces.AUTH
const selectedRegisterModule = ModuleNamespaces.SELECTED_REGISTER

@Component({
  components: {
    LexonTextFieldComponent,
    StaticAlertComponent,
    ValidationObserver
  }
})
export default class CustomerPortalComponent extends Mixins(PermissionsMixin) {
  @Prop({
    type: String,
    required: true
  })
  context!: string

  @Prop({
    type: Number
  })
  permissionsEntity!: number

  @Getter('getCustomerPortal', { namespace: customerPortalModule })
  customerPortalData: CustomerPortal

  @Getter('getSelectedRegisterId', { namespace: selectedRegisterModule })
  selectedRegisterId: (context: string) => number

  @Getter('getSelectedRegisterName', { namespace: selectedRegisterModule })
  selectedRegisterName: (context: string) => string

  @Getter('getSelectedRegisterEmail', { namespace: selectedRegisterModule })
  selectedRegisterEmail: (context: string) => string

  @Getter('getCustomerEmailPortal', { namespace: customerPortalModule })
  getCustomerEmailPortal: string

  @Getter('getCustomerPortalStatePortal', { namespace: customerPortalModule })
  statePortal: string | null

  @Getter('getCompanyId', { namespace: authModule })
  selectedCompanyId: number

  @Action('showDialog', { namespace: dialogModule })
  showDialog: ({}) => void

  @Action('activateCustomerPortal', { namespace: customerPortalModule })
  activateCustomerPortal: ({}) => Promise<void>

  @Action('lockCustomerPortal', { namespace: customerPortalModule })
  lockCustomerPortal: (customerId: string | number) => Promise<void>

  @Action('unlockCustomerPortal', { namespace: customerPortalModule })
  unlockCustomerPortal: (customerId: string | number) => Promise<void>

  @Action('deleteCustomerPortal', { namespace: customerPortalModule })
  deleteCustomerPortal: (customerId: string | number) => Promise<void>

  @Action('fetchCustomerPortal', { namespace: customerPortalModule })
  fetchCustomerPortal: (customerId: string | number) => CustomerPortal[]

  $refs!: {
    emailInput: InstanceType<typeof ValidationObserver>
  }

  icons = {
    lock: Icons.LOCK,
    doubleLock: Icons.DOUBLE_LOCK,
    unlock: Icons.UNLOCK,
    remove: Icons.REMOVE
  }

  email: string | null = ''
  name: string | null = ''
  customerId: string | number = ''

  texts = {
    alertActive: this.$t('components.customer_portal.success_alert'),
    alertBlock: this.$t('components.customer_portal.block_alert'),
    alertPending: this.$t('components.customer_portal.pending_alert')
  }

  alertPending: AlertsData = {
    type: AlertsTypes.INFO,
    message: this.texts.alertPending
  }
  alertActive: AlertsData = {
    type: AlertsTypes.SUCCESS,
    message: this.texts.alertActive
  }
  alertBlock: AlertsData = {
    type: AlertsTypes.ERROR,
    message: this.texts.alertBlock
  }

  stateMap: { [key in StatePortal]: AlertsData } = {
    [StatePortal.ACCESS_NOT_SENT]: this.alertPending,
    [StatePortal.ACCESS_NOT_ACTIVATED]: this.alertPending,
    [StatePortal.ACCESS_BLOCKED]: this.alertBlock,
    [StatePortal.ACCESS_ACTIVATED]: this.alertActive
  }

  get accessExists() {
    switch (this.statePortal) {
      case StatePortal.ACCESS_BLOCKED:
        return true
      case StatePortal.ACCESS_ACTIVATED:
        return true
    }
  }

  get credentialsSent() {
    return this.statePortal !== StatePortal.ACCESS_NOT_SENT
  }

  get alertData() {
    return this.stateMap[this.statePortal as StatePortal]
  }

  get showAdvice() {
    return this.statePortal !== StatePortal.ACCESS_NOT_SENT
  }

  get blockedAccess() {
    return this.statePortal === StatePortal.ACCESS_BLOCKED
  }

  get iconType() {
    switch (this.statePortal) {
      case StatePortal.ACCESS_BLOCKED:
        return this.icons.lock
      case StatePortal.ACCESS_ACTIVATED:
        return this.icons.unlock
      case StatePortal.ACCESS_NOT_ACTIVATED:
        return this.icons.doubleLock
      case StatePortal.ACCESS_NOT_SENT:
        return this.icons.lock
    }
  }

  get renderedText() {
    switch (this.statePortal) {
      case StatePortal.ACCESS_BLOCKED:
        return this.$t('components.customer_portal.blocked_access') as string
      case StatePortal.ACCESS_ACTIVATED:
        return this.$t('components.customer_portal.access_activated') as string
      case StatePortal.ACCESS_NOT_ACTIVATED:
        return this.$t('components.customer_portal.access_sent') as string
      case StatePortal.ACCESS_NOT_SENT:
        return this.$t('components.customer_portal.no_sent') as string
    }
  }

  get renderedPrimaryButtonText() {
    return this.statePortal === StatePortal.ACCESS_NOT_SENT
      ? this.$t('components.customer_portal.send_credentials_button')
      : this.$t('components.customer_portal.forward_access')
  }

  get renderedSecondaryButtonText() {
    return this.statePortal === StatePortal.ACCESS_BLOCKED
      ? this.$t('components.customer_portal.activate_access')
      : this.$t('components.customer_portal.block_access')
  }

  get getClasses() {
    return [
      { 'credentials-sent': this.statePortal === StatePortal.ACCESS_NOT_ACTIVATED },
      { 'access-exists': this.statePortal === StatePortal.ACCESS_ACTIVATED },
      { 'blocked-access': this.statePortal === StatePortal.ACCESS_BLOCKED },
      'access-container'
    ]
  }

  get canSave() {
    if (this.permissionsEntity) {
      const { canSave } = this.checkEntityPermissionsGetter(this.permissionsEntity)
      return canSave
    }
    return 1
  }

  @Watch('getCustomerEmailPortal')
  changeEmailValue(value: any) {
    this.email = value
  }

  mounted() {
    this.email = this.getCustomerEmailPortal || this.selectedRegisterEmail(this.context)
    this.name = this.selectedRegisterName(this.context) || this.customerPortalData.userPortal
    this.customerId = this.selectedRegisterId(this.context)
  }

  async deleteAccess() {
    await this.deleteCustomerPortal(this.customerId)
    this.fetchCustomerPortal(this.customerId)
  }

  askIfWantToDeleteAccess() {
    this.showDialog({
      type: DialogTypes.INFO,
      message: this.$t('components.customer_portal.delete_access_message'),
      action: this.deleteAccess
    })
  }

  async executeMainAction() {
    const isValidEmail = await this.$refs.emailInput.validate()
    if (!isValidEmail) {
      return
    }
    await this.activateCustomerPortal({
      id: this.customerId,
      name: this.name,
      portalEmail: this.email
    })
    this.fetchCustomerPortal(this.customerId)
  }

  async blockAccess() {
    await this.lockCustomerPortal(this.customerId)
    this.fetchCustomerPortal(this.customerId)
  }

  async lockOrUnlockPortalAccess() {
    if (this.blockedAccess) {
      await this.unlockCustomerPortal(this.customerId)
      this.fetchCustomerPortal(this.customerId)
    } else {
      this.showDialog({
        type: DialogTypes.INFO,
        message: this.$t('components.customer_portal.block_access_message'),
        action: this.blockAccess
      })
    }
  }
}
</script>

<style lang="scss">
.customer-portal-container {
  margin: auto;
  margin-bottom: 10px;
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr 1fr;
  grid-template-rows: auto auto auto;
  gap: 0px 0px;
  grid-auto-flow: row;
  grid-template-areas:
    'alert alert alert alert'
    'text text text text'
    'input input input btn'
    'btn2 btn2 . .';

  .intro-text {
    grid-area: text;
    font-family: $secondary-font;
    font-size: 13px;
    line-height: 20px;
    margin-bottom: 20px;
  }

  .access-container {
    @include flex;
    width: 100%;
    height: 50px;
    background-color: $blue-02;
    color: $white-01;
    margin: 25px 0 15px;

    .access-icon {
      font-size: 30px;
      margin: 0 30px 0 23px;
    }

    .access-status-text {
      font-size: 14px;
      font-family: $corporate-font-medium;
    }

    &.credentials-sent {
      background-color: $warning-color;
    }

    &.access-exists {
      background-color: $success-color;
    }

    &.blocked-access {
      background-color: $error-color;
    }
  }

  .email-field {
    width: 100%;
    grid-area: input;
  }

  .static-alert {
    width: 100%;
    grid-area: alert;
    margin-bottom: 20px;
  }

  .container-buttons {
    @include flex($flex-direction: row);
    align-self: flex-end;
    justify-content: start;
    gap: 10px;
    width: 100%;
    grid-area: btn2;
  }
  .secondary-btn {
    @include main-action-button--model2;
    min-width: 164px;
    margin-left: 0;
    padding: 0;

    &.activate-access {
      @include main-action-button--model2;
      min-width: 164px;
      margin-left: 0;
    }
    span {
      margin-left: 5px;
    }
  }
  .btn-area {
    grid-area: btn;
    display: flex;
    align-items: center;
    .primary-btn {
      @include main-action-button--model1;
      width: 100%;
    }
  }
}
</style>
